import { $createParagraphNode, $createTextNode, $getRoot, $isElementNode, $isParagraphNode, $isTextNode, } from 'lexical';
export function $appendTextToRoot(text, options) {
    const paragraph = getOrCreateParagraph();
    if (options?.trimExistingContent) {
        $trimParagraphTextContent(paragraph);
    }
    const prefix = paragraph.getTextContent().trim().length > 0 ? (options?.prefixWhenNonEmpty ?? '') : '';
    $appendTextWithoutPrefix(paragraph, `${prefix}${text}`);
}
export function $replaceTextSuffixOrAppend({ prefixWhenNonEmpty, suffix, text, }) {
    const paragraph = getOrCreateParagraph();
    const replacedSuffix = suffix ? $removeTextSuffix(paragraph, suffix) : false;
    const prefix = !replacedSuffix && paragraph.getTextContent().trim().length > 0
        ? (prefixWhenNonEmpty ?? '')
        : '';
    $appendTextWithoutPrefix(paragraph, `${prefix}${text}`);
}
function $appendTextWithoutPrefix(paragraph, text) {
    if (text) {
        paragraph.append($createTextNode(text));
    }
    $getRoot().selectEnd();
}
function $removeTextSuffix(paragraph, suffix) {
    if (!paragraph.getTextContent().endsWith(suffix)) {
        return false;
    }
    const leaves = getTextContentLeaves(paragraph);
    if (!canRemoveTextFromLeavesEnd(leaves, suffix.length)) {
        return false;
    }
    $removeTextFromLeavesEnd(leaves, suffix.length, paragraph);
    return true;
}
function $trimParagraphTextContent(paragraph) {
    $trimParagraphTextStart(paragraph);
    $trimParagraphTextEnd(paragraph);
}
function $trimParagraphTextStart(paragraph) {
    for (const node of getTextContentLeaves(paragraph)) {
        const text = node.getTextContent();
        const trimmed = text.trimStart();
        if (trimmed.length === 0) {
            removeNodeAndEmptyParents(node, paragraph);
            continue;
        }
        if ($isTextNode(node) && trimmed.length !== text.length) {
            node.spliceText(0, text.length - trimmed.length, '');
        }
        return;
    }
}
function $trimParagraphTextEnd(paragraph) {
    for (const node of getTextContentLeaves(paragraph).reverse()) {
        const text = node.getTextContent();
        const trimmed = text.trimEnd();
        if (trimmed.length === 0) {
            removeNodeAndEmptyParents(node, paragraph);
            continue;
        }
        if ($isTextNode(node) && trimmed.length !== text.length) {
            node.spliceText(trimmed.length, text.length - trimmed.length, '');
        }
        return;
    }
}
function getOrCreateParagraph() {
    const paragraph = getParagraph();
    if (paragraph) {
        return paragraph;
    }
    const newParagraph = $createParagraphNode();
    $getRoot().append(newParagraph);
    return newParagraph;
}
function getParagraph() {
    return ($getRoot()
        .getChildren()
        .find((child) => $isParagraphNode(child)) ?? null);
}
function getTextContentLeaves(node) {
    if ($isTextNode(node)) {
        return [node];
    }
    if (!$isElementNode(node)) {
        return [];
    }
    return node.getChildren().flatMap((child) => {
        if ($isTextNode(child)) {
            return [child];
        }
        if ($isElementNode(child)) {
            return getTextContentLeaves(child);
        }
        return child.getTextContent().length > 0 ? [child] : [];
    });
}
function canRemoveTextFromLeavesEnd(leaves, length) {
    let remainingLength = length;
    for (const node of leaves.toReversed()) {
        if (remainingLength <= 0) {
            return true;
        }
        const textLength = node.getTextContent().length;
        if ($isTextNode(node)) {
            remainingLength -= Math.min(textLength, remainingLength);
            continue;
        }
        if (remainingLength < textLength) {
            return false;
        }
        remainingLength -= textLength;
    }
    return remainingLength === 0;
}
function $removeTextFromLeavesEnd(leaves, length, stopAt) {
    let remainingLength = length;
    for (const node of leaves.toReversed()) {
        if (remainingLength <= 0) {
            return;
        }
        const textLength = node.getTextContent().length;
        if ($isTextNode(node)) {
            const deleteLength = Math.min(textLength, remainingLength);
            node.spliceText(textLength - deleteLength, deleteLength, '');
            if (node.getTextContentSize() === 0) {
                removeNodeAndEmptyParents(node, stopAt);
            }
            remainingLength -= deleteLength;
            continue;
        }
        removeNodeAndEmptyParents(node, stopAt);
        remainingLength -= textLength;
    }
}
function removeNodeAndEmptyParents(node, stopAt) {
    let parent = node.getParent();
    node.remove();
    while (parent && parent !== stopAt && $isElementNode(parent) && parent.getChildrenSize() === 0) {
        const nextParent = parent.getParent();
        parent.remove();
        parent = nextParent;
    }
}
//# sourceMappingURL=editorTextOperations.js.map